import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { ICursorElements } from '@/store/types/search.types';
import { TAppError } from '@/store/types/base.state';

import { KYCService } from '@/features/kyc/services/KYCService';
import { IKycDocument, IKycDocumentPreview, TKycDocumentStatusType } from '@/features/kyc/store/models/kyc.model';
import {
  TKycNeedReviewAction,
  IKycNeedReviewState,
  KycActionTypes,
} from '@/features/kyc/store/types/kyc-need-review.types';

export const getKycNeedReviewDocuments = (
  cursor?: string,
): ThunkAction<Promise<void>, IKycNeedReviewState, {}, TKycNeedReviewAction> => {
  const request = (): TKycNeedReviewAction => ({
    type: KycActionTypes.GET_KYC_NEED_REVIEW_DOCUMENTS,
  });
  const fail = (error: TAppError): TKycNeedReviewAction => ({
    type: KycActionTypes.GET_KYC_NEED_REVIEW_DOCUMENTS_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/users/kyc.html#doing-your-part',
  });
  const success = (needReviewDocuments: ICursorElements<IKycDocument>): TKycNeedReviewAction => ({
    type: KycActionTypes.GET_KYC_NEED_REVIEW_DOCUMENTS_SUCCESS,
    shouldSkipToaster: true,
    needReviewDocuments,
  });

  return async (dispatch: ThunkDispatch<IKycNeedReviewState, {}, TKycNeedReviewAction>): Promise<void> => {
    dispatch(request());
    try {
      const needReviewDocuments = await KYCService.getNeedReviewKycDocument(cursor);
      dispatch(success(needReviewDocuments));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getKycNeedReviewDocumentPreview = (
  documentId: string,
): ThunkAction<Promise<void>, IKycNeedReviewState, {}, TKycNeedReviewAction> => {
  const request = (): TKycNeedReviewAction => ({
    type: KycActionTypes.GET_KYC_NEED_REVIEW_DOCUMENT_PREVIEW,
    documentId,
  });
  const fail = (error: TAppError): TKycNeedReviewAction => ({
    type: KycActionTypes.GET_KYC_NEED_REVIEW_DOCUMENT_PREVIEW_FAIL,
    documentId,
    error,
    documentation: 'https://docs.treezor.com/guide/users/documents.html#download',
  });
  const success = (preview: IKycDocumentPreview): TKycNeedReviewAction => ({
    type: KycActionTypes.GET_KYC_NEED_REVIEW_DOCUMENT_PREVIEW_SUCCESS,
    shouldSkipToaster: true,
    documentId,
    preview,
  });

  return async (dispatch: ThunkDispatch<IKycNeedReviewState, {}, TKycNeedReviewAction>): Promise<void> => {
    dispatch(request());
    try {
      const preview = await KYCService.getPreviewKycDocument(documentId);

      dispatch(success(preview));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const reviewNeedReviewDocument = (
  status: TKycDocumentStatusType,
  document: IKycDocument,
  comment: string,
): ThunkAction<Promise<void>, IKycNeedReviewState, {}, TKycNeedReviewAction> => {
  const request = (): TKycNeedReviewAction => ({
    type: KycActionTypes.REVIEW_NEED_REVIEW_DOCUMENT,
    document,
  });
  const fail = (error: TAppError): TKycNeedReviewAction => ({
    type: KycActionTypes.REVIEW_NEED_REVIEW_DOCUMENT_FAIL,
    document,
    error,
    documentation: 'https://docs.treezor.com/guide/users/documents.html#download',
  });
  const success = (document: IKycDocument): TKycNeedReviewAction => ({
    type: KycActionTypes.REVIEW_NEED_REVIEW_DOCUMENT_SUCCESS,
    document,
  });

  return async (dispatch: ThunkDispatch<IKycNeedReviewState, {}, TKycNeedReviewAction>): Promise<void> => {
    dispatch(request());
    try {
      const documentStatus = status === 'VALIDATED' ? 2 : 1;
      const documentKyc = await KYCService.editKycStatusDocument(document.documentId, documentStatus, comment);
      dispatch(success(documentKyc));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};
